// Pure day-of logic for the attendee schedule "Up next" hero. Kept out of the
// component so the now/next/done selection and the countdown formatting can be
// unit-tested against a fixed clock. All times are interpreted in LOCAL time, to
// stay consistent with the existing isNow() logic on the schedule page.
import type { AttendeeMeetingDto } from '../../types/lobbyDay'
import { localDateTime } from '../../lib/localDateTime'

/** Minutes before a meeting's start at which it begins counting as "happening now". */
const NOW_LEAD_MINUTES = 15


export function meetingStart(m: AttendeeMeetingDto): Date {
  return localDateTime(m.meetingDate, m.startTime)
}

export function meetingEnd(m: AttendeeMeetingDto): Date {
  return localDateTime(m.meetingDate, m.endTime)
}

export type FocusKind = 'now' | 'next' | 'done'

export interface FocusSelection {
  kind: FocusKind
  meeting: AttendeeMeetingDto | null
}

/** True when a meeting is within its live window: NOW_LEAD_MINUTES before start through end. */
function isActive(m: AttendeeMeetingDto, now: Date): boolean {
  const start = meetingStart(m).getTime()
  const end = meetingEnd(m).getTime()
  return now.getTime() >= start - NOW_LEAD_MINUTES * 60_000 && now.getTime() < end
}

/**
 * Pick the one meeting to feature: an active one ("now"), else the soonest future
 * one ("next"), else nothing ("done"). Ties broken by earliest start.
 */
export function selectFocusMeeting(meetings: AttendeeMeetingDto[], now: Date): FocusSelection {
  const byStart = [...meetings].sort((a, b) => meetingStart(a).getTime() - meetingStart(b).getTime())

  const active = byStart.find((m) => isActive(m, now))
  if (active) return { kind: 'now', meeting: active }

  const next = byStart.find((m) => meetingStart(m).getTime() > now.getTime())
  if (next) return { kind: 'next', meeting: next }

  return { kind: 'done', meeting: null }
}

/**
 * Split the remaining meetings (everything but the focus) into upcoming (not yet
 * ended, chronological) and past (ended, most-recent-first).
 */
export function partitionRest(
  meetings: AttendeeMeetingDto[],
  now: Date,
  focusMeetingId: string | null,
): { upcoming: AttendeeMeetingDto[]; past: AttendeeMeetingDto[] } {
  const rest = meetings.filter((m) => m.meetingId !== focusMeetingId)
  const upcoming = rest
    .filter((m) => meetingEnd(m).getTime() >= now.getTime())
    .sort((a, b) => meetingStart(a).getTime() - meetingStart(b).getTime())
  const past = rest
    .filter((m) => meetingEnd(m).getTime() < now.getTime())
    .sort((a, b) => meetingStart(b).getTime() - meetingStart(a).getTime())
  return { upcoming, past }
}

/** Human magnitude of a duration: "25 min", "1 hr 5 min", "2 hr", "1 day", "3 days". */
export function formatCountdown(ms: number): string {
  const totalMin = Math.max(0, Math.round(ms / 60_000))
  const dayMin = 24 * 60
  if (totalMin >= dayMin) {
    const days = Math.round(totalMin / dayMin)
    return `${days} ${days === 1 ? 'day' : 'days'}`
  }
  const hr = Math.floor(totalMin / 60)
  const min = totalMin % 60
  if (hr === 0) return `${min} min`
  if (min === 0) return `${hr} hr`
  return `${hr} hr ${min} min`
}

/** True when the meeting falls on now's local calendar day. */
export function startsToday(m: AttendeeMeetingDto, now: Date): boolean {
  const start = meetingStart(m)
  return (
    start.getFullYear() === now.getFullYear() &&
    start.getMonth() === now.getMonth() &&
    start.getDate() === now.getDate()
  )
}
